import { I18nService } from '@/core/services/i18nService';
import { AceZahlungAceZahlungEnumsAceZahlungStatuscode, AnfrageDisplayModel, GlobalEnumerationsAceErfolgtausstehend, GlobalEnumerationsAceJaNein, GlobalEnumerationsAceMittelabruf, GlobalEnumerationsAceStatuszuwendungsbest, PaymentDisplayModel, PortalkonfigurationDisplayModel } from '@/api/model-index';
import { DialogRef, DialogService } from '@progress/kendo-angular-dialog';
import { AnfrageService } from '@/api/service-index';
import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { NumberFormatOptions } from '@progress/kendo-angular-intl';
import { Constants } from '@/constants';
import { LoadingAnimationService } from '@/core/services/loading-animation.service';
import { MittelabrufDialogComponent } from '../../dialogs/mittelabruf-dialog/mittelabruf-dialog.component';
import { UntypedFormControl } from '@angular/forms';
import { PaymentService } from '@/core/services/payment.service';
import moment from 'moment';
import { ComponentService } from '@/core/services/component.service';



@Component({
    selector: 'app-funding-table',
    templateUrl: './funding-table.component.html',
    styleUrls: ['./funding-table.component.scss'],
    standalone: false
})
export class FundingTableComponent {
    @Input()
    public type: GlobalEnumerationsAceMittelabruf;

    @Input()
    public anfrage: AnfrageDisplayModel;

    @Input()
    public portalkonfiguration: PortalkonfigurationDisplayModel;

    @Input()
    public foerdersummeOffen: number | null | undefined = 0;
    @Output()
    public foerdersummeOffenChange = new EventEmitter<number>();

    public mittelabrufDialog: DialogRef;
    public currencyFormatOptions: NumberFormatOptions = Constants.CURRENCY_FORMAT_OPTIONS;

    public get payments(): PaymentDisplayModel[] {
        return this.paymentService.payments;
    }

    public get isZahlung(): boolean {
        return this.type === GlobalEnumerationsAceMittelabruf.Zahlung;
    }

    public get isFoerderbetrag(): boolean {
        return this.type === GlobalEnumerationsAceMittelabruf.Forderbetrag;
    }

    constructor(
        protected anfrageService: AnfrageService,
        protected dialogService: DialogService,
        protected loadingAnimationService: LoadingAnimationService,
        protected paymentService: PaymentService,
        protected translateService: I18nService
    ) { }

    public zahlungNichtErfolgt(value: number): boolean {
        return value !== GlobalEnumerationsAceErfolgtausstehend.erfolgt;
    }

    public formatPaymentAmount(amount: any): string | null {
        return amount ? this.translateService.currency(amount) : null;
    }

    public mittelAbrufbar(payment: PaymentDisplayModel): boolean {
        if (this.anfrage.mittelabrufSperren === GlobalEnumerationsAceJaNein.Ja)
            return false;

        // Wenn kein Zahlungsdatum vorhanden, dann ist der Mittelabruf immer möglich
        if (this.nullOrUndefined(payment.paymentDate))
            return true;

        // Wenn beide Fristen nicht gesetzt sind, dann ist der Mittelabruf immer möglich
        if (this.nullOrUndefined(this.portalkonfiguration.fristMittelabrufAb) && this.nullOrUndefined(this.portalkonfiguration.fristMittelabrufBis))
            return true;

        // Wenn das Zahlungsdatum in der Frist liegt, dann ist der Mittelabruf möglich
        // Als Fallback wird das Zahlungsdatum verwendet
        let fristAb = this.portalkonfiguration.fristMittelabrufAb ? moment(payment.paymentDate).subtract(this.portalkonfiguration.fristMittelabrufAb, 'days') : moment(payment.paymentDate);
        let fristBis = this.portalkonfiguration.fristMittelabrufBis ? moment(payment.paymentDate).add(this.portalkonfiguration.fristMittelabrufBis, 'days') : moment(payment.paymentDate);

        return moment().isBetween(fristAb, fristBis, "days");
    }

    private nullOrUndefined(value: any): boolean {
        return value === null || value === undefined;
    }

    // Zuwendungsbestätigung
    public zwb(value: number): string {
        if (!value)
            return "";
        return this.translateService.getEnumValue(GlobalEnumerationsAceStatuszuwendungsbest, value)?.Description ?? "";
    }

    // Mittelabruf
    public mittelabruf(value: number): string {
        if (!value)
            return "-";
        return this.translateService.getEnumValue(GlobalEnumerationsAceErfolgtausstehend, value)?.Description ?? "";
    }

    public statusgrund(value: number): string {
        if (! value)
            return "-";
        return this.translateService.getEnumValue(AceZahlungAceZahlungEnumsAceZahlungStatuscode, value)?.Description ?? "";
    }

    protected componentService = inject(ComponentService);

    public async openNewFundsPaymentDialog(payment: PaymentDisplayModel | null) {
        let subscription = this.anfrageService.apiAnfrageAnfrageIdAttachmentGet({ anfrageId: this.anfrage.id, paymentId: payment?.id, loadContent: false, loadDocumentType: true }).subscribe(response => {
            let documentsForm: UntypedFormControl = new UntypedFormControl();

            documentsForm.setValue(response);

            this.mittelabrufDialog = this.dialogService.open({
                content: this.componentService.get(MittelabrufDialogComponent),
                width: "90%",
                maxWidth: Constants.DIALOG.MAX_WIDTH,
                maxHeight: Constants.DIALOG.MAX_HEIGHT,
                title: this.translateService.instant("enquiry-card.callForFunds")
            });

            if (!this.anfrage.objektTyp)
                throw new Error("Anfrage has no objektTyp");

            let dialog: MittelabrufDialogComponent = this.mittelabrufDialog.content.instance;

            dialog.anfrageId = this.anfrage.id;
            dialog.payment = payment;
            dialog.dokumententyp = this.portalkonfiguration.mittelabrufDokumententyp;
            dialog.documentsForm = documentsForm;
            dialog.scope = this.anfrage.objektTyp;
            dialog.type = this.type;
            dialog.portalkonfiguration = this.portalkonfiguration;
            dialog.foerdersummeOffen = this.foerdersummeOffen;
            dialog.foerdersummeOffenChange = this.foerdersummeOffenChange;

            return this.mittelabrufDialog;
        });

        this.loadingAnimationService.showSpinnerUsingSubscription(subscription);
    }
}
